import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Download, Linkedin, Quote } from 'lucide-react';

const BOOK_PDF_URL =
  'https://customer-assets.emergentagent.com/job_2af900fb-a4c0-4108-9990-fef980765d9c/artifacts/jzui1v18_THE%20GUARDIANS%20OF%20TRANSMISSION%20PAULIN%20Laurent%20%281%29.pdf';
const LINKEDIN_URL = 'https://www.linkedin.com/in/laurent-paulin/';

export const Book = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="book"
      data-testid="book-section"
      ref={ref}
      className="py-16 lg:py-20 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-center bg-slate-900 rounded-3xl p-8 lg:p-14 relative overflow-hidden"
        >
          {/* Decorative quote */}
          <Quote className="absolute top-6 right-6 w-20 h-20 text-amber-500/10 rotate-180" />

          {/* Book cover */}
          <div className="lg:col-span-2 flex justify-center lg:justify-start">
            <motion.div
              initial={{ scale: 0.9, rotate: -4 }}
              animate={isInView ? { scale: 1, rotate: -4 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative max-w-xs w-full"
            >
              <div className="aspect-[2/3] rounded-xl shadow-2xl bg-gradient-to-br from-amber-500 via-amber-600 to-amber-700 p-1">
                <div className="w-full h-full rounded-lg bg-gradient-to-br from-slate-800 to-slate-900 flex flex-col items-center justify-center text-center p-6 relative overflow-hidden">
                  <BookOpen className="w-12 h-12 text-amber-400 mb-4" />
                  <p className="font-caveat text-amber-400 text-lg mb-2">
                    {t('book.bookByline')}
                  </p>
                  <h3 className="font-playfair text-white text-2xl lg:text-3xl font-bold leading-tight italic">
                    The Guardians<br />of Transmission
                  </h3>
                  <div className="w-12 h-px bg-amber-400/60 my-5" />
                  <p className="font-inter text-slate-300 text-sm uppercase tracking-widest">
                    Laurent Paulin
                  </p>
                </div>
              </div>
              {/* Floating accent */}
              <div className="absolute -bottom-3 -right-3 bg-amber-500 text-slate-900 font-bold text-xs uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg">
                {t('book.exclusive')}
              </div>
            </motion.div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3 text-white">
            <span className="text-amber-400 font-inter text-sm font-semibold tracking-wider uppercase">
              {t('book.tag')}
            </span>
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 mb-5 leading-tight">
              {t('book.title1')} <span className="italic text-amber-400">{t('book.titleItalic')}</span>
            </h2>
            <p className="font-inter text-slate-300 text-base lg:text-lg leading-relaxed mb-4">
              {t('book.p1')}
            </p>
            <p className="font-inter text-slate-400 text-sm leading-relaxed mb-8">
              {t('book.p2')}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={BOOK_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-download-btn"
                className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-full px-6 py-3 transition-colors btn-shine"
              >
                <Download className="w-5 h-5" />
                {t('book.downloadBtn')}
              </a>
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-linkedin-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#0A66C2] hover:bg-[#0959AB] text-white font-semibold rounded-full px-6 py-3 transition-colors"
              >
                <Linkedin className="w-5 h-5" />
                {t('book.linkedinBtn')}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
